/**
 * Shared GST registration and conditional ITC inputs for claim review steps.
 * @module components/ClaimGstItcFields
 */
import { forwardRef, useEffect, useId, useImperativeHandle, useMemo, useState } from 'react'
import { YesNoToggle } from './ui'
import { persistWindscreenGst, readClaimDraft } from '../claims/claimDraftStorage'
import { isClaimGstItcBlockComplete, parseClaimItcPercentage } from '../claims/claimItcPercentage'
import {
  claimFormFieldClass,
  CLAIM_FORM_FIELD_ERROR_RING_WRAPPER_CLASS,
  ClaimFormFieldError,
  ClaimFormSubmitSummaryAlert,
} from '../claims/claimFormValidationFeedback'
import { useScrollToTopOnErrorSignal } from '../hooks/useScrollToTopOnErrorSignal'

function persistGstPatch(
  policyRef: string | null,
  gstAnswer: 'Yes' | 'No' | null,
  itcAnswer: 'Yes' | 'No' | null,
  itcPctParsed: number | null
): void {
  if (gstAnswer === null) return
  const gstRegistered = gstAnswer === 'Yes'
  persistWindscreenGst(policyRef, {
    gstRegistered,
    itcEntitled: gstRegistered ? (itcAnswer === 'Yes' ? true : itcAnswer === 'No' ? false : null) : null,
    itcPercentage: gstRegistered && itcAnswer === 'Yes' && itcPctParsed !== null ? itcPctParsed : null,
  })
}

export type ClaimGstItcFieldsHandle = {
  /** Writes current answers to session draft immediately (for submit after debounced autosave). */
  flushToDraft: () => void
}

export type ClaimGstItcFieldsProps = {
  policyRef: string | null
  /** Called whenever completeness of GST / ITC / percentage changes. */
  onValidityChange?: (complete: boolean) => void
  /** When true and fields incomplete, shows summary alert and highlights required controls. */
  submitAttempted?: boolean
}

const ITC_PCT_INPUT_CLASS =
  'w-full rounded-xl border border-solid border-border-primary bg-background-primary px-[14px] py-[12px] font-body text-sm leading-sm text-text-primary-900 shadow-xs placeholder:text-text-placeholder focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground-brand-solid focus-visible:ring-offset-2'

/**
 * GST registration and conditional ITC (same copy and rules as the legacy dedicated GST step).
 * Persists to {@link readClaimDraft} via {@link persistWindscreenGst} with a short debounce.
 */
const ClaimGstItcFields = forwardRef<ClaimGstItcFieldsHandle, ClaimGstItcFieldsProps>(function ClaimGstItcFields(
  { policyRef, onValidityChange, submitAttempted = false },
  ref
) {
  const headingId = useId()
  const itcHeadingId = useId()
  const itcPctFieldId = useId()
  const gstFieldsetId = useId()
  const itcFieldsetId = useId()

  const [gstAnswer, setGstAnswer] = useState<'Yes' | 'No' | null>(() => {
    const r = readClaimDraft()?.windscreenGstRegistered
    if (r === true) return 'Yes'
    if (r === false) return 'No'
    return null
  })
  const [itcAnswer, setItcAnswer] = useState<'Yes' | 'No' | null>(() => {
    const e = readClaimDraft()?.windscreenItcEntitled
    if (e === true) return 'Yes'
    if (e === false) return 'No'
    return null
  })
  const [itcPctInput, setItcPctInput] = useState(() => {
    const p = readClaimDraft()?.windscreenItcPercentage
    if (typeof p === 'number' && Number.isInteger(p) && p >= 1 && p <= 100) return String(p)
    return ''
  })

  const itcPctParsed = useMemo(() => parseClaimItcPercentage(itcPctInput), [itcPctInput])

  const showItcBlock = gstAnswer === 'Yes'
  const showItcPercentage = gstAnswer === 'Yes' && itcAnswer === 'Yes'

  const complete = useMemo(
    () => isClaimGstItcBlockComplete(gstAnswer, itcAnswer, itcPctParsed),
    [gstAnswer, itcAnswer, itcPctParsed]
  )

  const showSubmitSummary = submitAttempted && !complete
  const gstError = submitAttempted && gstAnswer === null
  const itcError = submitAttempted && showItcBlock && itcAnswer === null
  const itcPctError = submitAttempted && showItcPercentage && itcPctParsed === null

  useEffect(() => {
    onValidityChange?.(complete)
  }, [complete, onValidityChange])

  useEffect(() => {
    const t = window.setTimeout(() => {
      persistGstPatch(policyRef, gstAnswer, itcAnswer, itcPctParsed)
    }, 400)
    return () => window.clearTimeout(t)
  }, [policyRef, gstAnswer, itcAnswer, itcPctParsed])

  useImperativeHandle(ref, () => ({
    flushToDraft: () => {
      persistGstPatch(policyRef, gstAnswer, itcAnswer, itcPctParsed)
    },
  }))

  useScrollToTopOnErrorSignal(
    submitAttempted && showItcPercentage && itcPctParsed === null
      ? 'claim-gst-itc-percentage-invalid'
      : null
  )

  const handleGstChange = (value: 'Yes' | 'No') => {
    setGstAnswer(value)
    if (value === 'No') {
      setItcAnswer(null)
      setItcPctInput('')
    }
  }

  const handleItcChange = (value: 'Yes' | 'No') => {
    setItcAnswer(value)
    if (value === 'No') {
      setItcPctInput('')
    }
  }

  return (
    <section className="flex w-full flex-col gap-[16px]" aria-labelledby={headingId}>
      {showSubmitSummary ? <ClaimFormSubmitSummaryAlert /> : null}
      <h2
        id={headingId}
        className="m-0 font-display font-semibold text-display-xs leading-display-xs text-text-primary-900"
      >
        Are you registered for GST?
      </h2>
      <fieldset
        id={gstFieldsetId}
        className={`m-0 w-full min-w-0 border-0 p-0 ${gstError ? CLAIM_FORM_FIELD_ERROR_RING_WRAPPER_CLASS : ''}`}
      >
        <legend className="sr-only">Are you registered for GST? Choose Yes or No.</legend>
        <YesNoToggle value={gstAnswer ?? undefined} onChange={handleGstChange} />
      </fieldset>
      {gstError ? (
        <ClaimFormFieldError id={`${gstFieldsetId}-error`}>Choose Yes or No to continue.</ClaimFormFieldError>
      ) : null}
      <p className="m-0 font-body text-xs leading-xs text-text-tertiary-600">
        We collect this in case your claim results in a total loss or cash settlement.
      </p>

      {showItcBlock ? (
        <div className="flex w-full flex-col gap-[12px] border-t border-border-secondary pt-[16px]">
          <h2
            id={itcHeadingId}
            className="m-0 font-display font-semibold text-display-xs leading-display-xs text-text-primary-900"
          >
            Are you entitled to claim an input tax credit (ITC)?
          </h2>
          <fieldset
            id={itcFieldsetId}
            className={`m-0 w-full min-w-0 border-0 p-0 ${itcError ? CLAIM_FORM_FIELD_ERROR_RING_WRAPPER_CLASS : ''}`}
          >
            <legend className="sr-only">Are you entitled to claim an input tax credit? Choose Yes or No.</legend>
            <YesNoToggle value={itcAnswer ?? undefined} onChange={handleItcChange} />
          </fieldset>
          {itcError ? (
            <ClaimFormFieldError id={`${itcFieldsetId}-error`}>Choose Yes or No to continue.</ClaimFormFieldError>
          ) : null}

          {showItcPercentage ? (
            <div className="flex w-full flex-col gap-[8px]">
              <label htmlFor={itcPctFieldId} className="font-body text-sm leading-sm text-text-tertiary-600">
                What percentage of ITC are you entitled to claim?
              </label>
              <div className="relative w-full">
                <input
                  id={itcPctFieldId}
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={100}
                  step={1}
                  autoComplete="off"
                  placeholder="1–100"
                  aria-invalid={itcPctError}
                  aria-describedby={itcPctError ? `${itcPctFieldId}-error` : undefined}
                  value={itcPctInput}
                  onChange={(e) => setItcPctInput(e.target.value)}
                  className={claimFormFieldClass(`${ITC_PCT_INPUT_CLASS} pr-[42px]`, itcPctError)}
                />
                <span
                  className="pointer-events-none absolute inset-y-0 right-[14px] flex items-center font-body text-sm font-semibold leading-sm text-text-tertiary-600"
                  aria-hidden
                >
                  %
                </span>
              </div>
              {itcPctError ? (
                <ClaimFormFieldError id={`${itcPctFieldId}-error`}>
                  Enter a valid percentage between 1 and 100.
                </ClaimFormFieldError>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  )
})

ClaimGstItcFields.displayName = 'ClaimGstItcFields'

export default ClaimGstItcFields
